import { getList } from '@/requests/api/get-list';
import {
  type ApartmentContract,
  type ApartmentContractRevenueIncreaseRow,
} from '@pigello/pigello-matrix';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';

// Custom handler for Invoice
export async function handleCustomApartmentContractRevenueIncreaseRowNestedCase(
  listResponse: TCustomList<ApartmentContractRevenueIncreaseRow>
): Promise<TCustomList<ApartmentContractRevenueIncreaseRow>> {
  const sourceObjectIds = listResponse.data.reduce<Set<string>>(
    (acc, revenueIncreaseRow) => {
      if (revenueIncreaseRow.revenueRowSource?.contract?.id) {
        acc.add(revenueIncreaseRow.revenueRowSource.contract.id);
      }

      return acc;
    },
    new Set()
  );

  if (sourceObjectIds.size === 0) return listResponse;

  const chunkedSourceIds = chunkIds(sourceObjectIds);
  const resolvedSourcesPromises = await Promise.allSettled(
    chunkedSourceIds.map((chunk) =>
      getList<ApartmentContract>({
        modelName: 'apartmentcontract',
        queryParams: {
          page: 1,
          pageSize: chunk.length,
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
        },
      })
    )
  );

  const resolvedSourceObjects: ApartmentContract[] = [];
  const failedSourceObjects: unknown[] = [];
  for (const promise of resolvedSourcesPromises) {
    if (promise.status === 'fulfilled') {
      resolvedSourceObjects.push(...(promise.value.list ?? []));
    } else {
      failedSourceObjects.push(promise.reason);
    }
  }

  // Update revenue row with source object
  const mappedListResponse: TCustomList<ApartmentContractRevenueIncreaseRow> = {
    data: listResponse.data.map((revenueIncreaseRow) => {
      const sourceContractId =
        revenueIncreaseRow.revenueRowSource?.contract?.id;
      if (!sourceContractId || !revenueIncreaseRow.revenueRowSource?.id) {
        return revenueIncreaseRow;
      }

      const sourceContract = resolvedSourceObjects.find(
        (sourceObject) => sourceObject.id === sourceContractId
      );
      if (!sourceContract) return revenueIncreaseRow;

      return {
        ...revenueIncreaseRow,
        revenueRowSource: {
          ...revenueIncreaseRow.revenueRowSource,
          contract: sourceContract,
        },
      };
    }) as ApartmentContractRevenueIncreaseRow[],
  };

  return mappedListResponse;
}
